import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import { Panel, PanelEmpty } from './DashboardUI';
import { SidebarContext } from '../../../context/SidebarContext';
import {
  CHART_BASE,
  CHART_THEME,
  CHART_GRID_NO_X,
  CHART_PALETTE,
  paletteAt
} from './theme';
import { cn } from '../../../utils/helper';

/**
 * Dense analytics primitives.
 *
 * A `Panel` wrapping a full-size ApexCharts chart is the right unit for one
 * headline series, but a dashboard that has to show ten breakdowns at once
 * needs smaller units. These three are those units — a compact donut, a single
 * outsized number, and a labelled bar list — so pages compose breakdowns
 * instead of hand-rolling a card per metric.
 *
 * All three take the same `[{ label, count }]` shape the sleep/sound endpoints
 * return, so a new backend distribution becomes a one-line addition.
 */

const sum = (items = []) => items.reduce((total, item) => total + (item.count || 0), 0);

const share = (count, total) => (total > 0 ? Math.round((count / total) * 100) : 0);

/**
 * The hex values Apex needs for the current theme.
 *
 * Charts cannot use Tailwind's `dark:` classes — Apex writes colours straight
 * into SVG attributes — so the theme has to be read in JS. Taken from the same
 * context the Darkmode switch writes to, which is what makes a chart recolour
 * on toggle instead of on reload.
 */
export const useChartTheme = () => {
  const { isDarkModeEnabled } = useContext(SidebarContext) || {};
  return isDarkModeEnabled ? CHART_THEME.dark : CHART_THEME.light;
};

const average = (values = []) =>
  values.length ? values.reduce((total, value) => total + (value || 0), 0) / values.length : 0;

/**
 * Compact donut with the total in the hole and a ranked legend underneath.
 *
 * The legend is capped at `legendLimit` rows and rolls the tail into "Other",
 * because the point of this card is the top of the distribution — the full
 * breakdown belongs in a full-size Panel.
 */
export const MiniDonutCard = ({
  title,
  subtitle,
  data = [],
  colors = CHART_PALETTE,
  unit = '',
  totalLabel = 'Total',
  legendLimit = 3,
  className = ''
}) => {
  const total = sum(data);
  const theme = useChartTheme();

  // Ranked for the legend, but the donut keeps the caller's order so an
  // ordered scale (quality, duration bands) reads worst → best.
  const ranked = data
    .map((item, index) => ({ ...item, color: colors[index % colors.length] || paletteAt(index) }))
    .filter((item) => item.count > 0)
    .sort((a, b) => b.count - a.count);

  const shown = ranked.slice(0, legendLimit);
  const restCount = ranked.slice(legendLimit).reduce((acc, item) => acc + item.count, 0);

  const options = {
    chart: { ...CHART_BASE, type: 'donut', sparkline: { enabled: true } },
    labels: data.map((item) => item.label),
    colors,
    // A 2px gap in the card's own colour, so segments separate without a
    // white line appearing across a dark card.
    stroke: { width: 2, colors: [theme.surface] },
    legend: { show: false },
    dataLabels: { enabled: false },
    plotOptions: {
      pie: {
        donut: {
          // 90% hole: the ring is a tenth of the radius, matching the design.
          // A thicker ring reads as a pie with a hole punched in it and makes
          // the card square; this reads as a ring with a figure inside.
          size: '90%',
          labels: {
            show: true,
            // Label above the figure, not below it: the design reads
            // "Top Category / Calm" downwards, and Apex's default puts the
            // value on top, so both offsets are inverted from the default.
            name: {
              show: true,
              offsetY: -10,
              fontSize: '12px',
              color: theme.label,
              // The hole always states the total, never the hovered slice.
              formatter: () => totalLabel
            },
            value: {
              show: true,
              offsetY: 12,
              fontSize: '16px',
              fontWeight: 500,
              // Apex defaults this to near-black, which vanished on a dark card.
              color: theme.value,
              formatter: () => total.toLocaleString()
            },
            total: {
              show: true,
              showAlways: true,
              label: totalLabel,
              fontSize: '12px',
              color: theme.label,
              formatter: () => total.toLocaleString()
            }
          }
        }
      }
    },
    tooltip: {
      y: { formatter: (val) => `${val.toLocaleString()}${unit ? ` ${unit}` : ''}` }
    }
  };

  return (
    <Panel title={title} subtitle={subtitle} className={className}>
      {total > 0 ? (
        <div className="flex flex-col h-full">
          <Chart
            type="donut"
            height={120}
            width="100%"
            series={data.map((item) => item.count)}
            options={options}
          />
          <ul className="mt-4 space-y-1.5">
            {shown.map((item) => (
              <li key={item.label} className="flex items-center gap-2 text-sm">
                <span
                  className="w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-gray-500 dark:text-gray-400 truncate" title={item.label}>
                  {item.label}
                </span>
                <span className="ml-auto font-semibold text-shoorah-btn dark:text-white">
                  {share(item.count, total)}%
                </span>
              </li>
            ))}
            {restCount > 0 && (
              <li className="flex items-center gap-2 text-sm">
                <span className="w-2 h-2 rounded-full shrink-0 bg-gray-300" />
                <span className="text-gray-500 dark:text-gray-400">Other</span>
                <span className="ml-auto font-semibold text-shoorah-btn dark:text-white">
                  {share(restCount, total)}%
                </span>
              </li>
            )}
          </ul>
        </div>
      ) : (
        <PanelEmpty>No data yet.</PanelEmpty>
      )}
    </Panel>
  );
};

MiniDonutCard.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  data: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, count: PropTypes.number })
  ),
  colors: PropTypes.arrayOf(PropTypes.string),
  unit: PropTypes.string,
  totalLabel: PropTypes.string,
  legendLimit: PropTypes.number,
  className: PropTypes.string
};

/**
 * One number, as large as the card allows — for the single figure a section is
 * ultimately judged on (share of nights hitting target, share of the library
 * nobody plays).
 */
export const BigStatPanel = ({ title, subtitle, value, caption, className = '' }) => (
  <Panel title={title} subtitle={subtitle} className={className}>
    <div className="flex flex-col justify-end h-full">
      <p className="text-[56px] leading-none font-semibold text-shoorah-btn dark:text-white tracking-tight">
        {value}
      </p>
      {caption && <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">{caption}</p>}
    </div>
  </Panel>
);

BigStatPanel.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  value: PropTypes.node,
  caption: PropTypes.node,
  className: PropTypes.string
};

/**
 * Labelled bars — a distribution read as a list rather than a chart.
 *
 * Bars are scaled against the largest bucket, not the total, so a long tail
 * stays visible; the number on the right is still the share of the total.
 */
export const ProgressList = ({ items = [], unit = '', color, className = '' }) => {
  const total = sum(items);
  const peak = items.reduce((max, item) => Math.max(max, item.count || 0), 0);

  if (!total) return <PanelEmpty>No data yet.</PanelEmpty>;

  return (
    <ul className={cn('space-y-4', className)}>
      {items.map((item, index) => (
        <li key={item.label}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm text-gray-600 dark:text-gray-300 truncate" title={item.label}>
              {item.label}
            </span>
            <span className="text-sm font-semibold text-shoorah-btn dark:text-white shrink-0">
              {share(item.count, total)}%
              <span className="ml-1.5 font-normal text-gray-400">
                {item.count.toLocaleString()}
                {unit ? ` ${unit}` : ''}
              </span>
            </span>
          </div>
          <div className="mt-1.5 h-2 rounded-full bg-shoorah-normanGray dark:bg-shoorah-darkBgColor overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${peak > 0 ? Math.max((item.count / peak) * 100, item.count > 0 ? 2 : 0) : 0}%`,
                backgroundColor: color || paletteAt(index)
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
};

ProgressList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, count: PropTypes.number })
  ),
  unit: PropTypes.string,
  color: PropTypes.string,
  className: PropTypes.string
};

/**
 * Horizontal bar chart for a `[{ label, count }]` distribution — the fallback
 * when a donut would have too many slices to read (7-bucket time-of-day
 * splits, weekday patterns).
 */
export const distributionBarOptions = ({ labels = [], colors, unit = '' }) => ({
  chart: { ...CHART_BASE, type: 'bar' },
  colors: colors || CHART_PALETTE,
  plotOptions: {
    bar: { horizontal: true, borderRadius: 4, barHeight: '62%', distributed: true }
  },
  dataLabels: { enabled: false },
  legend: { show: false },
  grid: CHART_GRID_NO_X,
  xaxis: { categories: labels },
  tooltip: { y: { formatter: (val) => `${val.toLocaleString()}${unit ? ` ${unit}` : ''}` } }
});

/**
 * Concentric arcs, one per measure, with the figures beside them.
 *
 * WHEN TO USE THIS INSTEAD OF A DONUT
 *
 * A donut splits one quantity into parts that sum to a whole. This does the
 * opposite job: several measures that are *not* parts of one total, shown
 * against a common scale so their relative size reads at a glance. A funnel
 * (everyone → active → paying) is the clearest case, because each ring is a
 * subset of the one outside it and the arcs nest the way the data does.
 *
 * Do not reach for it when the parts do sum to a whole — a reader will try to
 * add the arcs up, and a donut answers that question properly.
 *
 * SCALE
 *
 * `scale="max"` (the default) makes the largest measure a full sweep, so the
 * chart is about the measures' relation to each other. `scale="total"` makes
 * the sum a full sweep, for when each arc really is a share of everything.
 * Either way the legend states the raw figure, because an arc communicates
 * proportion and nothing else — the number is the thing people write down.
 *
 * Series order is the caller's: Apex draws the first entry as the outermost
 * ring, and for a funnel that has to be the largest, which is a decision the
 * data makes and not this component.
 */
export const RadialStatCard = ({
  title,
  subtitle,
  data = [],
  colors = CHART_PALETTE,
  unit = '',
  scale = 'max',
  showTrack = false,
  legendColumns = 2,
  action = null,
  className = ''
}) => {
  const theme = useChartTheme();
  const counts = data.map((item) => item.count || 0);
  const total = sum(data);
  const basis = scale === 'total' ? total : Math.max(...counts, 0);

  const series = counts.map((count) => (basis > 0 ? Math.round((count / basis) * 100) : 0));

  const options = {
    chart: { ...CHART_BASE, type: 'radialBar', sparkline: { enabled: true } },
    colors,
    labels: data.map((item) => item.label),
    // Rounded ends are what make a set of bare arcs read as one family rather
    // than as fragments of a broken ring.
    stroke: { lineCap: 'round' },
    plotOptions: {
      radialBar: {
        // A large hole is what keeps the arcs thin: Apex divides whatever is
        // left between hollow and edge among the rings, so a small hole gives
        // fat bands and a card that reads as a square rather than a dial.
        hollow: { size: data.length > 4 ? '46%' : '54%' },
        track: { show: showTrack, background: theme.grid, strokeWidth: '100%', margin: 5 },
        dataLabels: { show: false }
      }
    },
    legend: { show: false },
    tooltip: { enabled: false }
  };

  return (
    <Panel title={title} subtitle={subtitle} action={action} className={className}>
      {total > 0 ? (
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-full sm:w-[45%] shrink-0">
            <Chart type="radialBar" height={207} width="100%" series={series} options={options} />
          </div>

          <ul
            className={cn(
              'grid gap-x-[23px] gap-y-6 flex-1 min-w-0',
              legendColumns === 1 ? 'grid-cols-1' : 'grid-cols-1 ssm:grid-cols-2'
            )}
          >
            {data.map((item, index) => (
              <li key={item.label} className="min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: colors[index % colors.length] || paletteAt(index) }}
                  />
                  <span className="text-sm text-shoorah-gray3 truncate" title={item.label}>
                    {item.label}
                  </span>
                </div>
                <p className="mt-[9px] ps-[18px] text-xl font-medium text-shoorah-btn dark:text-shoorah-darkBg tabular-nums">
                  {(item.count || 0).toLocaleString()}
                  {unit ? (
                    <span className="ms-1 text-sm font-normal text-shoorah-gray3">{unit}</span>
                  ) : null}
                </p>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <PanelEmpty>No data yet.</PanelEmpty>
      )}
    </Panel>
  );
};

RadialStatCard.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  /** Measures, outermost ring first. */
  data: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, count: PropTypes.number })),
  colors: PropTypes.arrayOf(PropTypes.string),
  /** Appended after each legend figure ("users", "sessions"). */
  unit: PropTypes.string,
  /** 'max' — largest measure is a full sweep. 'total' — the sum is. */
  scale: PropTypes.oneOf(['max', 'total']),
  /** Draw the faint 100% ring behind each arc. Off by default. */
  showTrack: PropTypes.bool,
  legendColumns: PropTypes.oneOf([1, 2]),
  /** Slot in the panel header, e.g. a period selector. */
  action: PropTypes.node,
  className: PropTypes.string
};

/**
 * Radar: several groups compared across the same handful of measures.
 *
 * The shape is the point — a group that is strong everywhere covers the web,
 * one that is strong in a single measure is a spike — so this is for
 * comparison, not for reading values off. That is why the axes are unlabelled
 * and the figures live in the legend underneath.
 *
 * EVERY AXIS MUST SHARE A SCALE
 *
 * A radar silently lies when its axes do not, because the polygon's area
 * implies they are comparable. Values are therefore expected already
 * normalised to 0–100 by the caller — usually as a percentage of each group's
 * own size, which is what makes a 40-person company comparable with a
 * 40,000-person one. `display` carries the real figure for the legend when the
 * normalised number is not the one worth showing.
 *
 * Three to six axes. Two is a line, and past six the labels collide and the
 * shape stops being readable — a bar list is the better answer by then.
 */
export const RadarStatCard = ({
  title,
  subtitle,
  axes = [],
  series = [],
  colors = CHART_PALETTE,
  unit = '%',
  action = null,
  className = ''
}) => {
  const theme = useChartTheme();
  const hasData =
    axes.length >= 3 && series.some((entry) => (entry.values || []).some((v) => v > 0));

  const options = {
    chart: { ...CHART_BASE, type: 'radar' },
    colors,
    labels: axes,
    stroke: { width: 2 },
    fill: { opacity: 0.12 },
    markers: { size: 0 },
    legend: { show: false },
    dataLabels: { enabled: false },
    // A fixed 0–100 domain, so two of these cards side by side mean the same
    // thing. Left to itself Apex rescales to the data and the shapes stop
    // being comparable between cards.
    yaxis: { show: false, min: 0, max: 100 },
    xaxis: {
      // Axis labels are the bright text colour, not the muted one — they name
      // the measure, so they are content rather than chrome.
      labels: { style: { colors: axes.map(() => theme.value), fontSize: '11px' } }
    },
    plotOptions: {
      radar: {
        polygons: {
          strokeColors: theme.grid,
          connectorColors: theme.grid,
          fill: { colors: ['transparent'] }
        }
      }
    },
    tooltip: {
      y: { formatter: (value) => `${Math.round(value)}${unit}` }
    }
  };

  return (
    <Panel title={title} subtitle={subtitle} action={action} className={className}>
      {hasData ? (
        <div className="flex flex-col">
          <Chart
            type="radar"
            height={224}
            width="100%"
            options={options}
            series={series.map((entry) => ({ name: entry.label, data: entry.values || [] }))}
          />

          <ul className="mt-2 grid grid-cols-1 ssm:grid-cols-3 gap-x-5 gap-y-3">
            {series.map((entry, index) => (
              <li key={entry.label} className="min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: colors[index % colors.length] || paletteAt(index) }}
                  />
                  <span className="text-xs text-shoorah-gray3 truncate" title={entry.label}>
                    {entry.label}
                  </span>
                </div>
                <p className="mt-[9px] ps-[18px] text-base font-medium text-shoorah-btn dark:text-shoorah-darkBg tabular-nums">
                  {entry.display ?? `${Math.round(average(entry.values))}${unit}`}
                </p>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <PanelEmpty>Not enough data to compare yet.</PanelEmpty>
      )}
    </Panel>
  );
};

RadarStatCard.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  /** The measures, one per spoke. Three to six. */
  axes: PropTypes.arrayOf(PropTypes.string),
  /** One entry per group being compared; `values` align with `axes`. */
  series: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      values: PropTypes.arrayOf(PropTypes.number),
      /** Overrides the legend figure when the mean is not the point. */
      display: PropTypes.string
    })
  ),
  colors: PropTypes.arrayOf(PropTypes.string),
  unit: PropTypes.string,
  action: PropTypes.node,
  className: PropTypes.string
};
